import { categories } from '@features/Cashback/Calculator/constants/categories';
import { addViaPence } from '@features/Cashback/Calculator/utils/createCalcOutput';
import { getNonPartnerCap } from '@features/Cashback/Calculator/utils/getNonPartnerCap';
import { RetailPartner } from '@generated/gql/graphql';

import { type CalculationOutputCategory } from '../../types';
import { RetailerFields } from '../types';

const exponent = Math.pow(10, 2);

interface RetailerEarnings {
  brandId: string;
  categoryId: string;
  spend: number;
  isNonPartner: boolean;
  pence: number;
}

interface CalcCategoriesOptions {
  nonPartnerIds: Set<string>;
  coreServiceCount: number;
}

const calcRetailerEarnings = (
  retailers: RetailPartner[],
  formValues: RetailerFields,
  nonPartnerIds: Set<string>
): RetailerEarnings[] =>
  retailers.reduce<RetailerEarnings[]>((acc, brand) => {
    const categoryId = categories.find(
      (cat) => cat.name === brand.category
    )?.id;

    if (!categoryId) return acc;

    const spend = formValues[brand.id] ?? 0;
    const isNonPartner = nonPartnerIds.has(brand.id);

    return [
      ...acc,
      {
        brandId: brand.id,
        categoryId,
        spend,
        isNonPartner,
        pence: Math.round(spend * exponent * brand.discount),
      },
    ];
  }, []);

/**
 * Fill the non-partner cap retailer by retailer, trimming whichever retailers
 * run past it.
 */
const applyNonPartnerCap = (
  earnings: RetailerEarnings[],
  coreServiceCount: number
): RetailerEarnings[] => {
  let remainingPence = Math.round(
    getNonPartnerCap(coreServiceCount) * exponent
  );

  return earnings.map((entry) => {
    if (!entry.isNonPartner) return entry;

    const allowedPence = Math.min(entry.pence, remainingPence);
    remainingPence -= allowedPence;

    return { ...entry, pence: allowedPence };
  });
};

const calcCategoriesFromRetailers = (
  retailers: RetailPartner[],
  formValues: RetailerFields,
  { nonPartnerIds, coreServiceCount }: CalcCategoriesOptions
): CalculationOutputCategory[] => {
  const cappedEarnings = applyNonPartnerCap(
    calcRetailerEarnings(retailers, formValues, nonPartnerIds),
    coreServiceCount
  );

  return categories.map((category) => {
    const categoryEarnings = cappedEarnings.filter(
      ({ categoryId }) => categoryId === category.id
    );

    return {
      category: category.id,
      // both totals go via pence so we don't accumulate float errors across
      // the retailers in a category
      spend: categoryEarnings.reduce(
        (acc, { spend }) => addViaPence(acc, spend),
        0
      ),
      earnings:
        Math.round(
          categoryEarnings.reduce((acc, { pence }) => acc + pence, 0)
        ) / exponent,
      retailers: categoryEarnings.map(({ brandId }) => brandId),
    };
  });
};

export { calcCategoriesFromRetailers };
